import React from "react";

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <h3 className="text-lg font-semibold text-black dark:text-white">{title}</h3>
      <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
        {children}
      </div>
    </div>
  );
}

export default function ITGuideModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 shadow-2xl">

        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-neutral-800">
          <div>
            <h2 className="text-2xl font-bold text-black dark:text-white">
              Role → Policy Mapping
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Permission Management System — arhitektura, workflow i pravila.
            </p>
          </div>

          <button
            onClick={onClose}
            className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
          >
            Close
          </button>
        </div>

        <div className="p-6 space-y-10">

          {/* CORE MODEL */}
          <Section title="1. Core model (kako sistem razmišlja)">
            <div className="rounded-xl border p-4 bg-gray-50 dark:bg-neutral-900 font-medium">
              User → HR Person → Position → Role → Policy → Permission → Scope → Visible Module
            </div>

            <p>
              Sistem je <strong>HR-driven</strong>.  
              To znači:
            </p>

            <ul className="list-disc pl-5 space-y-1">
              <li>ne dodeljuješ prava useru direktno</li>
              <li>sve ide kroz HR strukturu</li>
              <li>promena pozicije automatski menja pristup</li>
            </ul>
          </Section>

          {/* RESPONSIBILITIES */}
          <Section title="2. HR vs IT odgovornost">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border p-4 rounded-xl">
                <h4 className="font-semibold mb-2">HR modul</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>kreira ljude (persons)</li>
                  <li>definiše pozicije</li>
                  <li>dodeljuje pozicije</li>
                  <li>organizacija i hijerarhija</li>
                </ul>
              </div>

              <div className="border p-4 rounded-xl">
                <h4 className="font-semibold mb-2">IT modul</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>definiše modules</li>
                  <li>definiše permissions</li>
                  <li>kreira policies</li>
                  <li>mapira Role → policy</li>
                  <li>definiše scope rules</li>
                </ul>
              </div>
            </div>
          </Section>

          {/* MODULES */}
          <Section title="3. Modules (aplikacioni nivo)">
            <p>
              Modul = funkcionalna celina sistema (CRM, HR, Finance, AR, itd).
            </p>

            <p>
              Modul mora da postoji da bi:
            </p>

            <ul className="list-disc pl-5 space-y-1">
              <li>imao permissions</li>
              <li>imao scope</li>
              <li>bio vidljiv u UI</li>
            </ul>
          </Section>

          {/* PERMISSIONS */}
          <Section title="4. Permissions (akcije)">
            <p>
              Permission definiše šta korisnik može da uradi.
            </p>

            <div className="rounded-xl border p-4 bg-gray-50 dark:bg-neutral-900">
              primer: <br />
              ar.read <br />
              ar.write <br />
              ar.approve
            </div>

            <p>
              Permission je vezan za modul.
            </p>
          </Section>

          {/* POLICIES */}
          <Section title="5. Policies (bundle)">
            <p>
              Policy je skup permissiona.
            </p>

            <p>
              Ne daješ permissione direktno useru → daješ policy.
            </p>

            <div className="rounded-xl border p-4 bg-gray-50 dark:bg-neutral-900">
              policy_ar_user → ar.read <br />
              policy_ar_manager → ar.read + ar.write + ar.approve
            </div>
          </Section>

          {/* Role → Policy Mapping */}
          <Section title="6. Role Policy Mapping">
            <p>
              Povezuje HR svet sa IT svetom.
            </p>

            <div className="rounded-xl border p-4 bg-gray-50 dark:bg-neutral-900">
              ceo → policy_super_admin <br />
              accountant → policy_ar_user
            </div>

            <p>
              Ako ovo ne postoji → user nema prava iako ima poziciju.
            </p>
          </Section>

          {/* SCOPE */}
          <Section title="7. Scope (vidljivost)">
            <p>
              Scope NE daje akcije — daje vidljivost podataka.
            </p>

            <ul className="list-disc pl-5 space-y-1">
              <li>self → samo svoje</li>
              <li>subtree → hijerarhija</li>
              <li>department → odeljenje</li>
              <li>full → sve</li>
            </ul>
          </Section>

          {/* WORKFLOW */}
          <Section title="8. Workflow (kako se sistem koristi)">
            <ol className="list-decimal pl-5 space-y-1">
              <li>kreiraš modul</li>
              <li>kreiraš permissions</li>
              <li>kreiraš policy</li>
              <li>dodaš permissions u policy</li>
              <li>mapiraš Role → policy</li>
              <li>dodaš scope rules</li>
              <li>proveriš u My Access Debug</li>
            </ol>
          </Section>

          {/* REAL EXAMPLE */}
          <Section title="9. Primer (AR modul)">
            <div className="rounded-xl border p-4 bg-gray-50 dark:bg-neutral-900">
              Modul: AR <br />
              Permissions: ar.read, ar.write <br />
              Policy: policy_ar_user <br />
              Mapping: accountant → policy_ar_user <br />
              Scope: accountant → AR → department
            </div>
          </Section>

          {/* DEBUG */}
          <Section title="10. Debug (kako naći problem)">
            <p>Uvek ideš ovim redom:</p>

            <ol className="list-decimal pl-5 space-y-1">
              <li>user ima HR person?</li>
              <li>ima aktivnu poziciju?</li>
              <li>pozicija ima role?</li>
              <li>role mapirane na policy?</li>
              <li>policy ima permissions?</li>
              <li>postoji scope?</li>
            </ol>
          </Section>

          {/* ACCESS PANEL */}
          <Section title="11. IT Access Panel (glavni ekran)">
            <p>
              Ovo je centralni pregled sistema:
            </p>

            <ul className="list-disc pl-5 space-y-1">
              <li>ko nema HR link</li>
              <li>ko nema poziciju</li>
              <li>ko nema policy</li>
              <li>ko nema permissions</li>
            </ul>
          </Section>

        </div>
      </div>
    </div>
  );
}